import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Send, Star } from 'lucide-react';
import { Button } from '../ui/Button';
import { formatMoney } from '../../utils/formatMoney';
import { createSoiDaoIdempotencyKey } from '../../utils/soiDaoUtils';
import { confirmPurchase, showError } from '../../utils/swal';

export function SoiDaoPlayBar({
  room,
  selected,
  balance,
  isAuthenticated,
  isSubmitting,
  message,
  onClear,
  onPlay,
}) {
  const idempotencyKeyRef = useRef(createSoiDaoIdempotencyKey());
  const selectedKey = selected.join(',');

  useEffect(() => {
    idempotencyKeyRef.current = createSoiDaoIdempotencyKey();
  }, [selectedKey]);

  const price = Number(room?.price) || 0;
  const count = selected.length;
  const total = price * count;
  const hasEnoughBalance = balance >= total;
  const canSubmit =
    Boolean(room) &&
    count > 0 &&
    isAuthenticated &&
    hasEnoughBalance &&
    !isSubmitting;

  const handlePlay = async () => {
    if (!room || count === 0 || !isAuthenticated || isSubmitting) return;

    if (!hasEnoughBalance) {
      await showError({
        title: 'ยอดไม่พอ',
        text: `ยอดเงินไม่เพียงพอ · คงเหลือ ${formatMoney(balance)}`,
      });
      return;
    }

    const confirmed = await confirmPurchase({
      title: 'ยืนยันซื้อดาว',
      html: `<p>ซื้อ <strong>${count}</strong> ดาว</p><p class="mt-1">ยอดรวม <strong>${formatMoney(total)}</strong></p><p class="mt-2 break-all font-mono text-xs text-gray-500">${[...selected].sort().join(', ')}</p>`,
      confirmText: `ชำระ ${formatMoney(total)}`,
    });
    if (!confirmed) return;

    onPlay({
      roomId: room.id,
      stars: [...selected].sort(),
      idempotencyKey: idempotencyKeyRef.current,
    }).then((ok) => {
      if (ok) {
        idempotencyKeyRef.current = createSoiDaoIdempotencyKey();
      }
    });
  };

  return (
    <div className="sticky bottom-0 z-20 -mx-4 border-t border-red-100 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] backdrop-blur sm:static sm:mx-0 sm:rounded-2xl sm:border sm:border-red-100 sm:shadow-sm sm:backdrop-blur-none">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2 font-semibold text-brand-red">
            <Star className="h-4 w-4 fill-brand-gold text-brand-gold" />
            เลือก {count} ดาว
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">
              {formatMoney(price)} × {count}
            </p>
            <p className="text-lg font-bold text-brand-gold">
              รวม {formatMoney(total)}
            </p>
          </div>
        </div>

        {count > 0 ? (
          <p className="break-all font-mono text-xs text-gray-500">
            {selected.slice().sort().join(', ')}
          </p>
        ) : (
          <p className="text-xs text-gray-500">แตะดาวบนกระดานเพื่อเลือกตำแหน่ง</p>
        )}

        {!isAuthenticated ? (
          <p className="text-center text-sm text-brand-red">
            กรุณา{' '}
            <Link to="/login" className="font-semibold underline">
              เข้าสู่ระบบ
            </Link>{' '}
            ก่อนซื้อดาว
          </p>
        ) : null}

        {isAuthenticated && count > 0 && !hasEnoughBalance ? (
          <p className="text-center text-sm text-brand-red">
            ยอดเงินไม่เพียงพอ · คงเหลือ {formatMoney(balance)} ·{' '}
            <Link to="/wallet" className="font-semibold underline">
              ไปฝากเงิน
            </Link>
          </p>
        ) : null}

        {message ? (
          <div
            className={`rounded-lg p-2.5 text-center text-sm ${
              message.includes('สำเร็จ')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-brand-red'
            }`}
          >
            {message}
          </div>
        ) : null}

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            disabled={count === 0 || isSubmitting}
            onClick={onClear}
          >
            ล้างเลือก
          </Button>
          <Button
            className="flex-[2]"
            size="lg"
            disabled={!canSubmit}
            onClick={handlePlay}
          >
            <Send className="h-4 w-4" />
            {isSubmitting ? 'กำลังชำระ...' : `ชำระ ${formatMoney(total)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
